<script>
    import { createEventDispatcher } from 'svelte';
    import { getCurrentStore } from '../../services/api';
    import Icon from '../icons/Icon.svelte';

    export let configuringProduct = null;
    export let editingItem = null;
    export let editingPendingIndex = -1;
    export let isConfiguringPizza = false;
    export let isPizzaHalfAndHalf = false;
    export let selectedSecondProduct = null;
    export let availableSecondFlavors = [];
    export let configQty = 1;
    export let configUnitPrice = 0;
    export let configTotalPrice = 0;
    export let configNotes = '';
    export let configSelectedModifiers = {};
    export let quickNoteChips = [];
    export let hasMeatDonenessInGroups = false;
    export let printDestination = 'kitchen';
    export let isSavingEdit = false;
    export let isSubmittingItem = false;
    export let allowHalfBroto = null;

    const dispatch = createEventDispatcher();

    // Filtro de grupos e opções ativas (ignora grupos desativados no cardápio)
    $: rawGroups = configuringProduct?.modifierGroups || configuringProduct?.modifier_groups || [];
    $: activeModifierGroups = rawGroups.filter(g => g && g.active !== 0 && g.active !== false);
    $: sizeGroup = activeModifierGroups.find(g => /tamanho/i.test(g.name || ''));
    $: otherModifierGroups = activeModifierGroups.filter(g => !sizeGroup || g.id !== sizeGroup.id);
    $: selectedSizeOption = sizeGroup ? (configSelectedModifiers[sizeGroup.id || sizeGroup.name] || [])[0] : null;
    $: isBrotoOrSmallSize = Boolean(
        isConfiguringPizza &&
        selectedSizeOption &&
        /\b(broto|pequena|individual|mini|\bp\b)\b/i.test(selectedSizeOption.name || '')
    );
    $: effectiveAllowHalfBroto = allowHalfBroto !== null
        ? Boolean(allowHalfBroto)
        : Boolean(getCurrentStore()?.allow_half_broto);
    $: shouldBlockBrotoHalf = isBrotoOrSmallSize && !effectiveAllowHalfBroto;

    $: if (shouldBlockBrotoHalf && isPizzaHalfAndHalf) {
        isPizzaHalfAndHalf = false;
        selectedSecondProduct = null;
    }

    function formatCurrency(val) {
        const num = parseFloat(val);
        return isNaN(num) ? '0,00' : num.toFixed(2).replace('.', ',');
    }

    function getProductBasePrice(prod) {
        if (!prod) return 0;
        const promo = parseFloat(prod.promo_price || prod.promoPrice || 0);
        const reg = parseFloat(prod.price || 0);
        return (promo > 0 && promo < reg) ? promo : reg;
    }

    function getEffectiveFlavorPrice(prod) {
        if (!prod) return 0;
        const base = getProductBasePrice(prod);
        if (!sizeGroup) return base;
        const gid = sizeGroup.id || sizeGroup.name;
        const selectedSize = (configSelectedModifiers[gid] || [])[0];
        if (!selectedSize) return base;

        if (prod.id === configuringProduct?.id) {
            return base + parseFloat(selectedSize.price || 0);
        }

        const groups = prod.modifierGroups || prod.modifier_groups || [];
        const sGroup = groups.find(g => /tamanho/i.test(g.name || ''));
        if (sGroup && Array.isArray(sGroup.options)) {
            const targetName = (selectedSize.name || '').toLowerCase();
            const matched = sGroup.options.find(o => {
                if (o.id && selectedSize.id && String(o.id) === String(selectedSize.id)) return true;
                const oName = (o.name || '').toLowerCase();
                if (targetName.includes('broto') && oName.includes('broto')) return true;
                if (targetName.includes('grande') && oName.includes('grande')) return true;
                if (targetName.includes('pequena') && oName.includes('pequena')) return true;
                if (targetName.includes('média') && oName.includes('média')) return true;
                if (targetName.includes('media') && oName.includes('media')) return true;
                if (targetName.includes('gigante') && oName.includes('gigante')) return true;
                return false;
            }) || sGroup.options.find(o => (o.name || '').toLowerCase() === targetName);

            if (matched) {
                return base + parseFloat(matched.price || 0);
            }
        }
        return base + parseFloat(selectedSize.price || 0);
    }

    function isOptionSelected(selectedMap, group, opt) {
        if (!selectedMap || !group || !opt) return false;
        const gid = group.id || group.name;
        const selected = selectedMap[gid] || [];
        return selected.some(s => s.id === opt.id || s.name === opt.name);
    }

    function handleClose() {
        dispatch('close');
    }

    function handleToggleModifier(group, opt) {
        dispatch('toggleModifier', { group, opt });
    }

    function handleToggleQuickNote(chip) {
        dispatch('toggleQuickNote', { chip });
    }

    function handleSetPrintDestination(dest) {
        dispatch('setPrintDestination', { dest });
    }

    function handleSaveEdit() {
        dispatch('saveEdit');
    }

    function handleAddToDraft() {
        dispatch('addToDraft');
    }

    function handleConfirmAdd(shouldPrint) {
        dispatch('confirmAdd', { shouldPrint });
    }
</script>

{#if configuringProduct}
    <div class="modal-overlay configure-item-overlay" on:click|self={handleClose}>
        <div class="modal-card configure-item-card">
            <div class="modal-header">
                <div>
                    <span class="modal-badge-tag">{editingItem ? 'Edição de Item' : (editingPendingIndex >= 0 ? 'Edição de Rascunho' : 'Lançamento de Pedido')}</span>
                    <h3>{configuringProduct.name}</h3>
                    {#if configuringProduct.description}
                        <p class="config-desc">{configuringProduct.description}</p>
                    {/if}
                </div>
                <button class="close-btn" on:click={handleClose} type="button">
                    <Icon name="x" size={16} />
                </button>
            </div>

            <div class="modal-body config-body">
                <!-- Seletor em Destaque de Tamanho de Pizza (topo visual idêntico ao cardápio online) -->
                {#if isConfiguringPizza && sizeGroup}
                    {@const gid = sizeGroup.id || sizeGroup.name}
                    {@const currentSelected = configSelectedModifiers[gid] || []}
                    {@const activeOptions = (sizeGroup.options || []).filter(o => o && o.active !== 0 && o.active !== false)}
                    {#if activeOptions.length > 0}
                        <div class="config-section pizza-size-highlight-section">
                            <div class="group-header">
                                <div>
                                    <strong class="group-title">{sizeGroup.name}</strong>
                                    <span class="badge-required">Obrigatório</span>
                                </div>
                                <span class="group-limit-caption">Escolha 1</span>
                            </div>
                            <div class="options-grid" role="radiogroup">
                                {#each activeOptions as opt}
                                    {@const selected = isOptionSelected(configSelectedModifiers, sizeGroup, opt)}
                                    <button 
                                        type="button" 
                                        class="option-pill single-choice" 
                                        class:selected
                                        role="radio"
                                        aria-checked={selected}
                                        on:click={() => handleToggleModifier(sizeGroup, opt)}
                                    >
                                        <span class="pill-radio-indicator" class:checked={selected} aria-hidden="true">
                                            <span class="pill-radio-dot"></span>
                                        </span>
                                        <span class="opt-name">{opt.name}</span>
                                        {#if parseFloat(opt.price || 0) > 0}
                                            <span class="opt-price">+ R$ {formatCurrency(opt.price)}</span>
                                        {:else}
                                            <span class="opt-free">Grátis</span>
                                        {/if}
                                    </button>
                                {/each}
                            </div>
                        </div>
                    {/if}
                {/if}

                <!-- Seletor de Pizza Meio a Meio -->
                {#if isConfiguringPizza}
                    <div class="config-section pizza-split-section">
                        <div class="split-toggle-container">
                            <button 
                                type="button" 
                                class="split-toggle-btn" 
                                class:active={!isPizzaHalfAndHalf}
                                on:click={() => { isPizzaHalfAndHalf = false; selectedSecondProduct = null; }}
                            >
                                <Icon name="utensils" size={14} />
                                <span>1 Sabor (Inteira)</span>
                            </button>
                            <button 
                                type="button" 
                                class="split-toggle-btn" 
                                class:active={isPizzaHalfAndHalf}
                                disabled={shouldBlockBrotoHalf}
                                title={shouldBlockBrotoHalf ? 'Tamanho Broto ou Pequena permite apenas 1 sabor' : ''}
                                on:click={() => { if (!shouldBlockBrotoHalf) isPizzaHalfAndHalf = true; }}
                            >
                                <Icon name="utensils" size={14} />
                                <span>2 Sabores (Meio a Meio)</span>
                            </button>
                        </div>

                        {#if isBrotoOrSmallSize}
                            {#if effectiveAllowHalfBroto}
                                <div class="broto-hint-alert" style="margin-top: 8px; font-size: 12px; color: #15803d; background: #dcfce7; padding: 6px 10px; border-radius: 6px; display: flex; align-items: center; gap: 6px; border: 1px solid #bbf7d0;">
                                    <Icon name="check" size={13} />
                                    <span>Tamanho <strong>Broto / Pequena</strong> com 2 sabores liberado pela configuração da loja.</span>
                                </div>
                            {:else}
                                <div class="broto-hint-alert" style="margin-top: 8px; font-size: 12px; color: #b45309; background: #fef3c7; padding: 6px 10px; border-radius: 6px; display: flex; align-items: center; gap: 6px;">
                                    <Icon name="lightbulb" size={13} />
                                    <span>Tamanho <strong>Broto / Pequena</strong> permite apenas 1 sabor (inteira). Escolha Grande para Meio a Meio.</span>
                                </div>
                            {/if}
                        {/if}

                        {#if isPizzaHalfAndHalf}
                            <div class="pizza-split-details-panel">
                                <div class="split-flavors-summary">
                                    <div class="flavor-badge-row">
                                        <span class="flavor-half-tag">1/2</span>
                                        <strong class="flavor-title">{configuringProduct.name.replace(/^pizza\s+/i, '').trim()}</strong>
                                        <span class="flavor-price-hint">R$ {formatCurrency(getEffectiveFlavorPrice(configuringProduct))}</span>
                                    </div>
                                    <div class="flavor-badge-row">
                                        <span class="flavor-half-tag second">1/2</span>
                                        {#if selectedSecondProduct}
                                            <strong class="flavor-title">{selectedSecondProduct.name.replace(/^pizza\s+/i, '').trim()}</strong>
                                            <span class="flavor-price-hint">R$ {formatCurrency(getEffectiveFlavorPrice(selectedSecondProduct))}</span>
                                        {:else}
                                            <span class="flavor-missing-hint">Escolha o 2º sabor abaixo</span>
                                        {/if}
                                    </div>
                                </div>

                                <div class="split-rule-alert" style="display: flex; align-items: center; gap: 6px;">
                                    <Icon name="lightbulb" size={13} />
                                    <span><strong>Regra:</strong> Cobra pelo maior valor entre os 2 sabores</span>
                                </div>

                                <div class="second-flavor-picker">
                                    <label class="config-sublabel" for="second-flavor-picker-list">Selecione o 2º Sabor:</label>
                                    {#if availableSecondFlavors.length > 0}
                                        <div id="second-flavor-picker-list" class="second-flavors-grid">
                                            {#each availableSecondFlavors as f}
                                                {@const isSelected = selectedSecondProduct && selectedSecondProduct.id === f.id}
                                                {@const fPrice = getEffectiveFlavorPrice(f)}
                                                {@const p1Price = getEffectiveFlavorPrice(configuringProduct)}
                                                {@const diff = fPrice - p1Price}
                                                <button 
                                                    type="button" 
                                                    class="flavor-opt-card" 
                                                    class:selected={isSelected}
                                                    on:click={() => selectedSecondProduct = f}
                                                >
                                                    <div class="flavor-opt-header">
                                                        <span class="flavor-opt-name">{f.name.replace(/^pizza\s+/i, '').trim()}</span>
                                                        {#if isSelected}
                                                            <span class="flavor-check-icon">✓</span>
                                                        {/if}
                                                    </div>
                                                    <div class="flavor-opt-meta">
                                                        <span class="flavor-opt-price">R$ {formatCurrency(fPrice)}</span>
                                                        {#if diff > 0.009}
                                                            <span class="flavor-diff-tag higher">+ R$ {formatCurrency(diff)} (Maior valor)</span>
                                                        {:else}
                                                            <span class="flavor-diff-tag same">Incluso no valor</span>
                                                        {/if}
                                                    </div>
                                                </button>
                                            {/each}
                                        </div>
                                    {:else}
                                        <p class="empty-flavors-note">Nenhum outro sabor compatível disponível nesta categoria.</p>
                                    {/if}
                                </div>
                            </div>
                        {/if}
                    </div>
                {/if}

                <!-- Seletor de Quantidade Principal -->
                <div class="config-section">
                    <label class="config-label" for="stepper-config-qty">Quantidade</label>
                    <div id="stepper-config-qty" class="config-stepper-big">
                        <button 
                            type="button" 
                            class="stepper-big-btn" 
                            disabled={configQty <= 1} 
                            on:click={() => configQty = Math.max(1, configQty - 1)}
                        >−</button>
                        <span class="stepper-big-val">{configQty}</span>
                        <button 
                            type="button" 
                            class="stepper-big-btn" 
                            on:click={() => configQty = configQty + 1}
                        >+</button>
                    </div>
                </div>

                <!-- Grupos de Modificadores / Adicionais Ativos (Inativos são excluídos) -->
                {#if (isConfiguringPizza ? otherModifierGroups : activeModifierGroups).length > 0}
                    {#each (isConfiguringPizza ? otherModifierGroups : activeModifierGroups) as group}
                        {@const activeOptions = (group.options || []).filter(o => o && o.active !== 0 && o.active !== false)}
                        {#if activeOptions.length > 0}
                            {@const gid = group.id || group.name}
                            {@const currentSelected = configSelectedModifiers[gid] || []}
                            {@const maxAllowed = group.max != null ? group.max : (group.max_choice != null ? group.max_choice : 1)}
                            {@const isSingleChoice = maxAllowed === 1}
                            {@const isLimitReached = !isSingleChoice && currentSelected.length >= maxAllowed}
                            <div class="config-section modifier-group-section" class:limit-reached={isLimitReached}>
                                <div class="group-header">
                                    <div>
                                        <strong class="group-title">{group.name}</strong>
                                        {#if group.required}
                                            <span class="badge-required">Obrigatório</span>
                                        {:else}
                                            <span class="badge-optional">Opcional</span>
                                        {/if}
                                    </div>
                                    <span class="group-limit-caption" class:limit-reached-text={isLimitReached}>
                                        {#if isSingleChoice}
                                            Escolha 1
                                        {:else if isLimitReached}
                                            Limite atingido ({currentSelected.length}/{maxAllowed})
                                        {:else}
                                            Até {maxAllowed || 'ilimitado'} ({currentSelected.length}/{maxAllowed})
                                        {/if}
                                    </span>
                                </div>

                                <div class="options-grid" role={isSingleChoice ? "radiogroup" : "group"}>
                                    {#each activeOptions as opt}
                                        {@const selected = isOptionSelected(configSelectedModifiers, group, opt)}
                                        <button 
                                            type="button" 
                                            class="option-pill" 
                                            class:selected
                                            class:single-choice={isSingleChoice}
                                            class:multi-choice={!isSingleChoice}
                                            role={isSingleChoice ? "radio" : "checkbox"}
                                            aria-checked={selected}
                                            on:click={() => handleToggleModifier(group, opt)}
                                        >
                                            {#if isSingleChoice}
                                                <span class="pill-radio-indicator" class:checked={selected} aria-hidden="true">
                                                    <span class="pill-radio-dot"></span>
                                                </span>
                                            {:else}
                                                <span class="pill-check-indicator" class:checked={selected} aria-hidden="true">
                                                    {#if selected}
                                                        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                                                            <polyline points="20 6 9 17 4 12"></polyline>
                                                        </svg>
                                                    {/if}
                                                </span>
                                            {/if}
                                            <span class="opt-name">{opt.name}</span>
                                            {#if parseFloat(opt.price || 0) > 0}
                                                <span class="opt-price">+ R$ {formatCurrency(opt.price)}</span>
                                            {:else}
                                                <span class="opt-free">Grátis</span>
                                            {/if}
                                        </button>
                                    {/each}
                                </div>
                            </div>
                        {/if}
                    {/each}
                {/if}

                <!-- Observações Rápidas Contextuais -->
                {#if quickNoteChips && quickNoteChips.length > 0}
                    <div class="config-section">
                        <label class="config-label" for="quick-chips-box">Observações Rápidas (Toque para alternar)</label>
                        <div id="quick-chips-box" class="chips-container">
                            {#each quickNoteChips as chip}
                                {@const active = configNotes.split(',').map(s => s.trim()).includes(chip)}
                                <button 
                                    type="button" 
                                    class="note-chip" 
                                    class:active 
                                    on:click={() => handleToggleQuickNote(chip)}
                                >
                                    {chip}
                                </button>
                            {/each}
                        </div>
                    </div>
                {/if}

                <!-- Campo de Observação Escrita -->
                <div class="config-section">
                    <label class="config-label" for="free-config-notes">Observação / Instrução Adicional para a Cozinha</label>
                    <input 
                        id="free-config-notes"
                        type="text" 
                        class="form-input config-input" 
                        placeholder={hasMeatDonenessInGroups ? "Ex: Sem salada, molho à parte, cortar ao meio..." : "Ex: Ponto da carne, sem salada, molho à parte..."} 
                        bind:value={configNotes}
                    />
                </div>

                <!-- Destino da Impressão (Cozinha vs Caixa vs Ambos) -->
                <div class="config-section print-dest-section">
                    <label class="config-label" for="print-dest-pill-group-cfg">Destino do Cupom Térmico</label>
                    <div id="print-dest-pill-group-cfg" class="print-dest-pill-group">
                        <button 
                            type="button" 
                            class="dest-pill" 
                            class:active={printDestination === 'kitchen'} 
                            on:click={() => handleSetPrintDestination('kitchen')}
                        >
                            <Icon name="chef-hat" size={13} /> Cozinha
                        </button>
                        <button 
                            type="button" 
                            class="dest-pill" 
                            class:active={printDestination === 'cashier'} 
                            on:click={() => handleSetPrintDestination('cashier')}
                        >
                            <Icon name="dollar-sign" size={13} /> Caixa / Balcão
                        </button>
                        <button 
                            type="button" 
                            class="dest-pill" 
                            class:active={printDestination === 'both'} 
                            on:click={() => handleSetPrintDestination('both')}
                        >
                            <Icon name="printer" size={13} /> Ambos (Via Dupla)
                        </button>
                    </div>
                </div>
            </div>

            <div class="modal-footer config-footer">
                <div class="config-total-display">
                    <span class="config-unit-label">Un: R$ {formatCurrency(configUnitPrice)}</span>
                    <strong class="config-total-val">Total: R$ {formatCurrency(configTotalPrice)}</strong>
                </div>

                <div class="config-actions">
                    {#if editingItem || editingPendingIndex >= 0}
                        <button 
                            type="button" 
                            class="btn-primary" 
                            style="min-width: 180px;"
                            disabled={isSavingEdit}
                            on:click={handleSaveEdit}
                        >
                            <Icon name="check" size={14} /> {isSavingEdit ? 'Salvando...' : 'Salvar Alterações'}
                        </button>
                    {:else}
                        <button 
                            type="button" 
                            class="btn-action-outline btn-add-draft" 
                            title="Adiciona ao lote pendente de rascunho sem enviar à cozinha"
                            on:click={handleAddToDraft}
                        >
                            + Guardar no Rascunho
                        </button>
                        <button 
                            type="button" 
                            class="btn-primary kitchen-btn" 
                            class:cashier-btn={printDestination === 'cashier'}
                            disabled={isSubmittingItem}
                            title="Lança imediatamente o item na mesa e comanda"
                            on:click={() => handleConfirmAdd(true)}
                        >
                            {#if isSubmittingItem}
                                Lançando...
                            {:else}
                                <Icon name="zap" size={14} /> Lançar na Mesa Agora
                            {/if}
                        </button>
                    {/if}
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    .modal-overlay {
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(15, 23, 42, 0.6);
        backdrop-filter: blur(2px);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        overflow-y: auto;
        box-sizing: border-box;
        z-index: 9999;
    }

    .configure-item-card {
        max-width: 580px;
        width: 95%;
        max-height: calc(100vh - 32px);
        max-height: calc(100dvh - 32px);
        display: flex;
        flex-direction: column;
        background: #FFFFFF;
        border-radius: 12px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        overflow: hidden;
        box-sizing: border-box;
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 20px 24px 16px;
        border-bottom: 1px solid #E2E8F0;
        flex-shrink: 0;
    }

    .modal-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
        color: #0F172A;
    }

    .close-btn {
        background: transparent;
        border: none;
        color: #64748B;
        cursor: pointer;
        padding: 4px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s, color 0.15s;
    }

    .close-btn:hover {
        background: #F1F5F9;
        color: #0F172A;
    }

    .modal-badge-tag {
        display: inline-block;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        color: #FF6A00;
        background: #FFF4EC;
        padding: 2px 8px;
        border-radius: 4px;
        margin-bottom: 4px;
    }

    .config-desc {
        margin: 4px 0 0 0;
        font-size: 13px;
        color: #64748B;
        line-height: 1.4;
    }

    .config-body {
        padding: 16px 20px;
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 18px;
        box-sizing: border-box;
    }

    .config-section {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .config-label {
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #475569;
    }

    .config-sublabel {
        font-size: 12px;
        font-weight: 600;
        color: #64748B;
        margin-bottom: 4px;
        display: block;
    }

    /* MEIO A MEIO / PIZZA SPLIT */
    .pizza-split-section {
        background: #FFF7ED;
        border: 1.5px solid #FED7AA;
        border-radius: 12px;
        padding: 14px;
    }

    .split-toggle-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
    }

    .split-toggle-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 10px;
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        color: #475569;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .split-toggle-btn.active {
        background: #FF6A00;
        border-color: #FF6A00;
        color: #FFFFFF;
        box-shadow: 0 2px 4px rgba(255, 106, 0, 0.25);
    }

    .pizza-split-details-panel {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px dashed #FED7AA;
    }

    .split-flavors-summary {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
    }

    .flavor-badge-row {
        background: #FFFFFF;
        border: 1px solid #FED7AA;
        border-radius: 8px;
        padding: 8px 10px;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .flavor-half-tag {
        font-size: 10px;
        font-weight: 800;
        color: #EA580C;
        background: #FFEDD5;
        padding: 1px 6px;
        border-radius: 4px;
        width: fit-content;
    }

    .flavor-half-tag.second {
        color: #2563EB;
        background: #DBEAFE;
    }

    .flavor-title {
        font-size: 13px;
        color: #0F172A;
    }

    .flavor-price-hint {
        font-size: 11px;
        color: #64748B;
    }

    .flavor-missing-hint {
        font-size: 12px;
        color: #DC2626;
        font-style: italic;
    }

    .split-rule-alert {
        font-size: 12px;
        color: #C2410C;
        background: #FFEDD5;
        padding: 6px 10px;
        border-radius: 6px;
    }

    .second-flavors-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
        gap: 6px;
        max-height: 180px;
        overflow-y: auto;
    }

    .flavor-opt-card {
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 6px;
        padding: 8px;
        text-align: left;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        gap: 4px;
        transition: all 0.15s ease;
    }

    .flavor-opt-card.selected {
        border-color: #FF6A00;
        background: #FFF7ED;
        box-shadow: 0 0 0 2px rgba(255, 106, 0, 0.2);
    }

    .flavor-opt-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .flavor-opt-name {
        font-size: 12px;
        font-weight: 600;
        color: #0F172A;
    }

    .flavor-check-icon {
        color: #FF6A00;
        font-weight: 800;
        font-size: 12px;
    }

    .flavor-opt-meta {
        display: flex;
        flex-direction: column;
        gap: 1px;
    }

    .flavor-opt-price {
        font-size: 11px;
        font-weight: 700;
        color: #64748B;
    }

    .flavor-diff-tag {
        font-size: 10px;
        font-weight: 600;
    }

    .flavor-diff-tag.higher {
        color: #EA580C;
    }

    .flavor-diff-tag.same {
        color: #16A34A;
    }

    .empty-flavors-note {
        font-size: 12px;
        color: #94A3B8;
        font-style: italic;
    }

    /* STEPPER */
    .config-stepper-big {
        display: flex;
        align-items: center;
        gap: 12px;
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        padding: 4px 8px;
        width: fit-content;
    }

    .stepper-big-btn {
        width: 34px;
        height: 34px;
        border-radius: 6px;
        border: 1px solid #E2E8F0;
        background: #FFFFFF;
        color: #0F172A;
        font-size: 18px;
        font-weight: 700;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s;
    }

    .stepper-big-btn:hover:not(:disabled) {
        background: #F1F5F9;
    }

    .stepper-big-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .stepper-big-val {
        font-size: 16px;
        font-weight: 800;
        color: #0F172A;
        min-width: 24px;
        text-align: center;
    }

    /* MODIFIERS */
    .modifier-group-section {
        background: #FAFAFA;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        padding: 12px;
    }

    .group-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
    }

    .group-title {
        font-size: 13px;
        color: #0F172A;
        margin-right: 6px;
    }

    .badge-required {
        font-size: 10px;
        font-weight: 700;
        color: #DC2626;
        background: #FEE2E2;
        padding: 1px 6px;
        border-radius: 4px;
    }

    .badge-optional {
        font-size: 10px;
        font-weight: 600;
        color: #64748B;
        background: #F1F5F9;
        padding: 1px 6px;
        border-radius: 4px;
    }

    .group-limit-caption {
        font-size: 11px;
        color: #64748B;
    }

    .group-limit-caption.limit-reached-text {
        color: #EA580C;
        font-weight: 600;
    }

    .options-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        gap: 6px;
    }

    .option-pill {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 10px;
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        cursor: pointer;
        font-size: 12px;
        color: #334155;
        transition: all 0.15s ease;
        text-align: left;
    }

    .option-pill.selected {
        border-color: #FF6A00;
        background: #FFF7ED;
        color: #0F172A;
    }

    .pill-radio-indicator {
        width: 14px;
        height: 14px;
        border-radius: 50%;
        border: 1.5px solid #CBD5E1;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .pill-radio-indicator.checked {
        border-color: #FF6A00;
    }

    .pill-radio-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: transparent;
    }

    .pill-radio-indicator.checked .pill-radio-dot {
        background: #FF6A00;
    }

    .pill-check-indicator {
        width: 14px;
        height: 14px;
        border-radius: 3px;
        border: 1.5px solid #CBD5E1;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        color: #FFFFFF;
    }

    .pill-check-indicator.checked {
        border-color: #FF6A00;
        background: #FF6A00;
    }

    .opt-name {
        flex: 1;
        font-weight: 600;
    }

    .opt-price {
        font-weight: 700;
        color: #FF6A00;
        font-size: 11px;
    }

    .opt-free {
        color: #16A34A;
        font-weight: 600;
        font-size: 11px;
    }

    /* CHIPS & INPUT */
    .chips-container {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .note-chip {
        padding: 5px 10px;
        background: #F1F5F9;
        border: 1px solid #E2E8F0;
        border-radius: 6px;
        font-size: 12px;
        color: #475569;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .note-chip.active {
        background: #FF6A00;
        border-color: #FF6A00;
        color: #FFFFFF;
    }

    .config-input {
        height: 40px;
        padding: 0 12px;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        font-size: 13px;
        color: #0F172A;
        background: #FFFFFF;
        outline: none;
        box-sizing: border-box;
    }

    .config-input:focus {
        border-color: #FF6A00;
        box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.12);
    }

    /* DESTINO */
    .print-dest-pill-group {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }

    .dest-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 7px 12px;
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        color: #64748B;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .dest-pill.active {
        background: #0F172A;
        border-color: #0F172A;
        color: #FFFFFF;
    }

    /* FOOTER */
    .config-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-top: 1px solid #E2E8F0;
        background: #F8FAFC;
        gap: 12px;
        flex-wrap: wrap;
    }

    .config-total-display {
        display: flex;
        flex-direction: column;
    }

    .config-unit-label {
        font-size: 12px;
        color: #64748B;
    }

    .config-total-val {
        font-size: 18px;
        font-weight: 900;
        color: #0F172A;
    }

    .config-actions {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .btn-action-outline {
        height: 38px;
        padding: 0 14px;
        border: 1px solid #E2E8F0;
        background: #FFFFFF;
        color: #475569;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        transition: all 0.15s ease;
    }

    .btn-action-outline:hover {
        background: #F1F5F9;
        color: #0F172A;
    }

    .btn-primary {
        height: 38px;
        padding: 0 16px;
        border: none;
        background: #FF6A00;
        color: #FFFFFF;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        box-shadow: 0 2px 4px rgba(255, 106, 0, 0.25);
        transition: all 0.15s ease;
    }

    .btn-primary:hover:not(:disabled) {
        background: #EA580C;
    }

    .btn-primary:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        box-shadow: none;
    }

    .btn-primary.cashier-btn {
        background: #2563EB;
        box-shadow: 0 2px 4px rgba(37, 99, 235, 0.25);
    }

    .btn-primary.cashier-btn:hover:not(:disabled) {
        background: #1D4ED8;
    }

    @media (max-width: 767.98px) {
        .modal-card.configure-item-card {
            width: 100vw !important;
            max-width: 100vw !important;
            height: 100vh !important;
            height: 100dvh !important;
            max-height: 100dvh !important;
            border-radius: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            position: fixed !important;
            inset: 0 !important;
            z-index: 10001 !important;
            background: #FFFFFF !important;
        }

        .configure-item-card .modal-header {
            padding: 12px 14px !important;
            border-bottom: 1px solid #E2E8F0 !important;
            background: #FFFFFF !important;
        }

        .configure-item-card .close-btn {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            min-height: 44px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            border-radius: 8px !important;
            background: #F1F5F9 !important;
            color: #0F172A !important;
        }

        .config-body {
            flex: 1 !important;
            overflow-y: auto !important;
            padding: 14px 14px 90px 14px !important;
            -webkit-overflow-scrolling: touch !important;
        }

        .config-footer {
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            background: #FFFFFF !important;
            border-top: 1px solid #E2E8F0 !important;
            padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px)) 14px !important;
            box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08) !important;
            z-index: 10002 !important;
            flex-direction: row !important;
            justify-content: space-between !important;
            align-items: center !important;
        }

        .config-actions {
            display: flex !important;
            gap: 8px !important;
            flex: 1 !important;
            justify-content: flex-end !important;
        }

        .config-actions button {
            min-height: 44px !important;
            font-size: 13px !important;
            font-weight: 700 !important;
            border-radius: 8px !important;
        }
    }
</style>
